import { parseColor, randomColor, toHex, type RGBA } from './lib/color';
import { createBlock } from './ui/block';
import { createSliders } from './ui/sliders';
import { createScheme } from './ui/scheme';
import { createHistory } from './ui/history';
import { createPicker } from './ui/picker';
import { createContrast } from './ui/contrast';
import { toast } from './ui/toast';
import { showShortcuts } from './ui/help';
import { load, save } from './lib/storage';
import { initLang, setLang, currentLang, onLangChange, t } from './lib/i18n';
import './style.css';

const STORAGE_KEY = 'color-picker:current';
const THEME_KEY = 'color-picker:theme';

// ============================================================
// URL 参数分享
// ============================================================

function getColorFromUrl(): RGBA | null {
  const params = new URLSearchParams(window.location.search);
  const c = params.get('c');
  if (c) return parseColor(c);
  return null;
}

function updateUrl(c: RGBA): void {
  const url = new URL(window.location.href);
  url.searchParams.set('c', toHex(c).replace('#', ''));
  history.replaceState(null, '', url.toString());
}

function copyShareUrl(): void {
  navigator.clipboard.writeText(window.location.href).then(() => {
    toast(t('Copied {value}', { value: 'URL' }));
  }).catch(() => {
    toast(t('Copy failed'));
  });
}

// ============================================================
// 状态 + 更新器注册表
// ============================================================

const state: { current: RGBA } = {
  current: getColorFromUrl() ?? load<RGBA | null>(STORAGE_KEY, null) ?? randomColor()
};

const updaters: ((c: RGBA) => void)[] = [];

function setColor(c: RGBA): void {
  state.current = { ...c };
  save(STORAGE_KEY, state.current);
  updateUrl(c);
  for (const fn of updaters) fn(state.current);
}

// ============================================================
// 主题
// ============================================================

type Theme = 'light' | 'dark';

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

function currentTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function toggleTheme(): void {
  const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  save(THEME_KEY, next);
  themeBtn.textContent = next === 'dark' ? '☀' : '🌙';
}

let themeBtn!: HTMLButtonElement;
let langBtn!: HTMLButtonElement;
let footer!: HTMLElement;

function updateFooter(): void {
  if (!footer) return;
  footer.innerHTML =
    `<span>Huefetch · Color Picker</span>` +
    `<span class="app-footer__hint">🎯 = Eyedropper · C = Copy HEX · X = Random · <kbd>?</kbd> = Shortcuts</span>` +
    `<a href="mailto:rustandireng910@gmail.com" class="app-footer__link">Feedback</a>`;
}

// ============================================================
// 语言
// ============================================================

function toggleLang(): void {
  const next = currentLang() === 'zh' ? 'en' : 'zh';
  setLang(next);
  langBtn.textContent = next === 'zh' ? '中' : 'EN';
  langBtn.title = next === 'zh' ? 'Switch to English' : '切换到中文';
  // 重新渲染所有组件（刷新文本）
  for (const fn of updaters) fn(state.current);
  updateFooter();
}

// ============================================================
// UI 装配
// ============================================================

function build(): void {
  const app = document.getElementById('app');
  if (!app) throw new Error('#app 容器不存在');

  // Header
  const header = document.createElement('header');
  header.className = 'app-header';
  const logo = document.createElement('div');
  logo.className = 'app-header__logo';
  logo.textContent = '🎨 Huefetch';

  langBtn = document.createElement('button');
  langBtn.type = 'button';
  langBtn.className = 'btn btn--ghost';
  langBtn.textContent = currentLang() === 'zh' ? '中' : 'EN';
  langBtn.title = currentLang() === 'zh' ? 'Switch to English' : '切换到中文';
  langBtn.addEventListener('click', toggleLang);

  themeBtn = document.createElement('button');
  themeBtn.type = 'button';
  themeBtn.className = 'btn btn--ghost';
  themeBtn.title = t('Toggle theme');
  themeBtn.setAttribute('aria-label', t('Toggle theme'));
  themeBtn.textContent = currentTheme() === 'dark' ? '☀' : '🌙';
  themeBtn.addEventListener('click', toggleTheme);

  header.append(logo, langBtn, themeBtn);

  // Main
  const main = document.createElement('main');
  main.className = 'app-main';

  const block = createBlock(state.current);
  const sliders = createSliders(state.current);
  const scheme = createScheme(state.current);
  const contrast = createContrast(state.current);
  const history = createHistory(setColor);
  const picker = createPicker(
    setColor,
    setColor,
    () => setColor(randomColor(1))
  );

  // 收藏按钮
  const favBtn = (picker.root as HTMLElement & { _favBtn?: HTMLButtonElement })._favBtn;
  favBtn?.addEventListener('click', () => {
    (history.root as HTMLElement & { _toggleFavorite?: (c: RGBA) => void })._toggleFavorite?.(state.current);
  });

  // HEX 输入回写
  block.onHexCommit((hex) => {
    const parsed = parseColor(hex);
    if (parsed) {
      setColor(parsed);
    } else {
      toast(t('Invalid color format'));
      block.getHexInput().value = toHex(state.current);
    }
  });

  // Share 按钮
  block.onShare(copyShareUrl);

  // 滑块
  sliders.onChange(setColor);
  // 配色方案点选
  scheme.onPick(setColor);

  main.append(
    block.root,
    picker.root,
    sliders.root,
    scheme.root,
    contrast.root,
    history.root
  );
  app.append(header, main);

  // Footer
  footer = document.createElement('footer');
  footer.className = 'app-footer';
  updateFooter();
  app.appendChild(footer);

  // 注册 update
  updaters.push(block.update, sliders.update, scheme.update, contrast.update, history.update);

  // 语言切换时刷新所有组件
  onLangChange(() => {
    for (const fn of updaters) fn(state.current);
    block.updateTexts();
    picker.update();
    updateFooter();
    themeBtn.title = t('Toggle theme');
    themeBtn.setAttribute('aria-label', t('Toggle theme'));
  });

  // 首次同步：保证历史/收藏后立即显示
  history.record(state.current);

  // 快捷键
  document.addEventListener('keydown', onKey);
}

// ============================================================
// 快捷键
// ============================================================

function onKey(e: KeyboardEvent): void {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
  if (e.code === 'Space') {
    e.preventDefault();
    const btn = document.querySelector<HTMLButtonElement>('.picker .btn--primary');
    btn?.click();
  } else if (e.key === 'c' || e.key === 'C') {
    navigator.clipboard
      .writeText(toHex(state.current))
      .then(() => toast(t('Copied {value}', { value: toHex(state.current) })))
      .catch(() => toast(t('Copy failed')));
  } else if (e.key === 'x' || e.key === 'X') {
    setColor(randomColor(1));
  } else if (e.key === '?') {
    showShortcuts();
  }
}

// ============================================================
// 启动
// ============================================================

initLang();
applyTheme(load<Theme>(THEME_KEY, 'light'));
build();
